import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { registerRandomUser, loginUser } from '../services/api';

interface UserData {
  username: string;
  email: string;
  password: string;
  token?: string;
  [key: string]: any;
}

interface AuthContextType {
  currentUser: UserData | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<UserData>;
  logout: () => void;
  createRandomUser: () => Promise<UserData>;
  loading: boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<UserData | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Check if user exists in localStorage
    const storedUser = localStorage.getItem('whomii_user');
    if (storedUser) {
      setCurrentUser(JSON.parse(storedUser));
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  const createRandomUser = async (): Promise<UserData> => {
    try {
      setLoading(true);
      const response = await registerRandomUser();
      const userDataStr = localStorage.getItem('whomii_user');
      if (!userDataStr) {
        throw new Error('Failed to create user data');
      }
      const userData = JSON.parse(userDataStr) as UserData;
      setCurrentUser(userData);
      setIsAuthenticated(true);
      return userData;
    } catch (error) {
      console.error('Failed to create random user:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<UserData> => {
    try {
      setLoading(true);
      const response = await loginUser(email, password);
      const userData: UserData = {
        email,
        password,
        username: email.split('@')[0],
        ...response
      };
      setCurrentUser(userData);
      setIsAuthenticated(true);
      localStorage.setItem('whomii_user', JSON.stringify(userData));
      return userData;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Clear user data from localStorage
      localStorage.removeItem('whomii_user');
      
      // Reset all auth state
      setCurrentUser(null);
      setIsAuthenticated(false);
      
      // Clear any other auth-related data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_session');
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    currentUser,
    isAuthenticated,
    login,
    logout,
    createRandomUser,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider; 